/**
 * React Query hooks for Session Clues (Player)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cluesApi } from '@api/endpoints/clues'
import { useSessionStore } from '../stores/sessionStore'
import { sessionKeys } from './useSession'

// ===================
// Query Keys
// ===================

export const SESSION_CLUE_QUERY_KEYS = {
  status: (sessionId: string) => ['session-clues', sessionId, 'status'] as const,
}

// ===================
// Query Hooks
// ===================

/**
 * Get clue status for a session (polling-enabled)
 */
export function useClueStatus(sessionId: string | null, enabled = true) {
  return useQuery({
    queryKey: SESSION_CLUE_QUERY_KEYS.status(sessionId || ''),
    queryFn: () => cluesApi.getClueStatus(sessionId!),
    enabled: !!sessionId && enabled,
    refetchInterval: 10000, // Poll every 10s
  })
}

// ===================
// Mutation Hooks
// ===================

/**
 * Request a clue from the session pool
 */
export function useRequestClue(sessionId: string | null) {
  const queryClient = useQueryClient()
  const store = useSessionStore()

  return useMutation({
    mutationFn: () => {
      if (!sessionId) throw new Error('Session ID is required')
      return cluesApi.requestClue(sessionId)
    },
    onSuccess: (response) => {
      // Update session store with new clue state
      if (response) {
        store.setClueState(
          true, // still has available if they got one
          response.cluesUsedCount,
          response.totalPenaltySeconds,
        )
      }

      // Invalidate status and journal queries to get fresh data
      if (sessionId) {
        queryClient.invalidateQueries({
          queryKey: SESSION_CLUE_QUERY_KEYS.status(sessionId),
        })
        queryClient.invalidateQueries({
          queryKey: sessionKeys.journal(sessionId),
        })
      }
    },
  })
}
